/**
 * 日志级别枚举
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

/**
 * 日志配置接口
 */
export interface ILoggerConfig {
  level: LogLevel;
  prefix?: string;
  enableTimestamp?: boolean;
}

/**
 * 默认日志配置
 */
const DEFAULT_CONFIG: ILoggerConfig = {
  level: LogLevel.INFO,
  prefix: 'VISALL',
  enableTimestamp: true
};

/**
 * 当前日志配置
 */
let currentConfig: ILoggerConfig = { ...DEFAULT_CONFIG };

/**
 * 设置日志配置
 * @param config - 日志配置
 */
export function configure(config: Partial<ILoggerConfig>): void {
  currentConfig = { ...currentConfig, ...config };
}

/**
 * 格式化日志消息
 * @param level - 日志级别标签
 * @param message - 日志消息
 * @param args - 附加参数
 * @returns 格式化后的日志内容
 */
function formatMessage(level: string, message: string, ...args: unknown[]): string {
  const { prefix, enableTimestamp } = currentConfig;
  const timestamp = enableTimestamp ? `[${new Date().toISOString()}]` : '';
  const prefixStr = prefix ? `[${prefix}]` : '';
  const levelStr = `[${level}]`;

  let formattedMessage = `${timestamp}${prefixStr}${levelStr} ${message}`;

  if (args.length > 0) {
    try {
      const argStrings = args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      );
      formattedMessage += ` ${argStrings.join(' ')}`;
    } catch (err) {
      formattedMessage += ' [无法序列化的参数]';
    }
  }

  return formattedMessage;
}

/**
 * 调试级别日志
 * @param message - 日志消息
 * @param args - 附加参数
 */
export function debug(message: string, ...args: unknown[]): void {
  if (currentConfig.level <= LogLevel.DEBUG) {
    console.debug(formatMessage('DEBUG', message, ...args));
  }
}

/**
 * 信息级别日志
 * @param message - 日志消息
 * @param args - 附加参数
 */
export function info(message: string, ...args: unknown[]): void {
  if (currentConfig.level <= LogLevel.INFO) {
    console.info(formatMessage('INFO', message, ...args));
  }
}

/**
 * 警告级别日志
 * @param message - 日志消息
 * @param args - 附加参数
 */
export function warn(message: string, ...args: unknown[]): void {
  if (currentConfig.level <= LogLevel.WARN) {
    console.warn(formatMessage('WARN', message, ...args));
  }
}

/**
 * 错误级别日志
 * @param message - 日志消息
 * @param args - 附加参数
 */
export function error(message: string, ...args: unknown[]): void {
  if (currentConfig.level <= LogLevel.ERROR) {
    console.error(formatMessage('ERROR', message, ...args));
  }
}

/**
 * 创建具有特定前缀的日志记录器
 * @param prefix - 日志前缀
 * @returns 日志记录器对象
 */
export function createLogger(prefix: string) {
  return {
    debug: (message: string, ...args: unknown[]) => {
      if (currentConfig.level <= LogLevel.DEBUG) {
        console.debug(formatMessage('DEBUG', `[${prefix}] ${message}`, ...args));
      }
    },
    info: (message: string, ...args: unknown[]) => {
      if (currentConfig.level <= LogLevel.INFO) {
        console.info(formatMessage('INFO', `[${prefix}] ${message}`, ...args));
      }
    },
    warn: (message: string, ...args: unknown[]) => {
      if (currentConfig.level <= LogLevel.WARN) {
        console.warn(formatMessage('WARN', `[${prefix}] ${message}`, ...args));
      }
    },
    error: (message: string, ...args: unknown[]) => {
      if (currentConfig.level <= LogLevel.ERROR) {
        console.error(formatMessage('ERROR', `[${prefix}] ${message}`, ...args));
      }
    }
  };
}
